import PropTypes from 'prop-types';
import moment from 'moment';
import FileThumbnail from '../../../atoms/FileThumbnail';
import GenericModal from '../../../modals/GenericModal';

import { useState } from 'react';
import { Divider, Stack, Button, Typography, Chip, Checkbox, FormControlLabel } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DocumentViewer from '../DocumentsView/../../../atoms/DocumentViewer';
import { MIcon } from '../../../@material-extend';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';

/**
 * The `DocumentValidationModal` component renders a dialog with detailed information about a shift or resource.
 * It dynamically loads related data such as files, contacts, and shift details.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {boolean} props.open - Indicates if the modal is open.
 * @param {Function} props.onClose - Callback function for closing the modal.
 * @param {Function} props.onConfirm - Callback function for closing the modal.

 * @returns {JSX.Element} The rendered `DocumentValidationModal` component.
 */
const DocumentValidationModal = (props) => {
  const { open = false, onConfirm, onClose, onDownloadFallback, onViewedFileCallback, title, file, loading } = props;
  const [isChecked, setIsChecked] = useState(false);
  const [loadDocError, setLoadDocError] = useState(false);
  const [unsupportedFormat, setUnsupportedFormat] = useState(false);
  const { isLargeMobile } = useBreakpoints();
  const isValidationRequired = file.validated_at === null;
  const isValidatedDocument = Boolean(file.validated_at);
  const isNotViewedAtFile = file.viewed_at === null;
  const canShowValidation = isValidationRequired && !loadDocError;

  let chipLabel = 'À valider';
  if (!isValidationRequired) {
    const formatDate = isLargeMobile ? 'DD/MM/YYYY [à] HH:mm' : 'DD MMMM YYYY [à] HH:mm';
    chipLabel = `Validé le ${moment(file.validated_at).format(formatDate)}`;
  }

  const documentViewerMessage =
    isValidatedDocument || !isValidationRequired ? (
      'Téléchargez puis consultez le document'
    ) : (
      <>
        Téléchargez, consultez puis <b>validez</b> le document
      </>
    );

  const handleClose = () => {
    onClose();
  };
  const handleValidateDocument = () => {
    onConfirm();
  };
  const downloadFile = () => {
    if (!file?.url.length) {
      return false;
    }

    if (onDownloadFallback && isNotViewedAtFile) {
      onDownloadFallback();
    }
    window.open(file?.url, '_blank');
    return true;
  };
  const extension = file?.extension?.toLowerCase() ?? '';

  const modalActions = (
    <>
      {isValidationRequired && (
        <Button variant="contained" disabled={!isChecked} onClick={handleValidateDocument} loading={loading}>
          Valider le document
        </Button>
      )}
      <Button variant="text" onClick={handleClose}>
        Fermer
      </Button>
    </>
  );

  const handleLoadDocError = () => {
    setLoadDocError(true);
  };

  const handleUnsupportedFormat = () => {
    setUnsupportedFormat(true);
  };

  return (
    <GenericModal fullScreen title={title} actions={modalActions} maxWidth="xl" open={open} handleClose={handleClose}>
      <Stack sx={{ height: '100%', width: '100%', justifyContent: 'space-between', gap: 2 }}>
        <Stack className="page-header">
          <Divider />

          <Stack direction="row" alignItems="center" gap={1.25} py={1.5} sx={{ overflow: 'hidden', width: '100%' }}>
            {!isLargeMobile && <FileThumbnail ext={extension} showThumbnails src={file?.url} variant="neutral" />}
            <Stack sx={{ flexGrow: 1, justifyContent: 'start', overflow: 'hidden', width: '100%' }}>
              <Typography variant="body1" className="text-ellipsis" sx={{ fontWeight: 600 }}>
                {file?.filename}
              </Typography>
              {!!file.site_name && (
                <Typography variant="caption" color="textSecondary">
                  {file.site_name}
                </Typography>
              )}
              <Stack direction="row" gap={2}>
                <Stack sx={{ flexGrow: 1, gap: isLargeMobile ? 0.25 : 0.5 }}>
                  <Typography variant="caption" color="textSecondary">
                    Ajouté le{' '}
                    {isLargeMobile
                      ? moment(file.created_at).format('DD/MM/YY')
                      : moment(file.created_at).format('DD/MM/YYYY')}
                  </Typography>
                  {(isValidationRequired || isValidatedDocument) && (
                    <Chip
                      label={chipLabel}
                      color={isValidationRequired ? 'warning' : 'success'}
                      size="small"
                      sx={{ borderRadius: 0, alignSelf: 'start', mt: 0.5 }}
                    />
                  )}
                </Stack>

                {!loadDocError && isLargeMobile && !unsupportedFormat && (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    sx={{ alignSelf: 'center', px: 1 }}
                    onClick={downloadFile}
                  >
                    Télécharger
                  </Button>
                )}
              </Stack>
            </Stack>

            {!loadDocError && !isLargeMobile && !unsupportedFormat && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                sx={{ flexGrow: 0, alignSelf: 'center' }}
                onClick={downloadFile}
              >
                Télécharger
              </Button>
            )}
          </Stack>
          <Divider />
        </Stack>

        {/* Scrollable Document Viewer Section */}
        <Stack sx={{ flexGrow: 1, height: '100%', overflow: 'auto' }}>
          <DocumentViewer
            fileUrl={file.url}
            title={title}
            onViewed={onViewedFileCallback}
            onLoadError={handleLoadDocError}
            onUnsupportedFormat={handleUnsupportedFormat}
          >
            <Stack spacing={2} alignItems="center" sx={{ justifyContent: 'center', height: '100%' }}>
              {!loadDocError && unsupportedFormat ? (
                <>
                  <Typography variant="body1" align="center">
                    {documentViewerMessage}
                  </Typography>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadFile}>
                    Télécharger
                  </Button>
                </>
              ) : (
                <>
                  <MIcon icon="cancel" variant="error" fontSize={96} filled />
                  <Typography variant="h3">Impossible de charger le document</Typography>
                  <Typography color="text.disabled">Réessayez ou contactez votre administrateur</Typography>
                </>
              )}
            </Stack>
          </DocumentViewer>
        </Stack>

        {canShowValidation && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                disabled={isNotViewedAtFile}
              />
            }
            label={file.validation_text || 'Je confirme avoir lu, compris et approuvé ce document.'}
          />
        )}
      </Stack>
    </GenericModal>
  );
};

DocumentValidationModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  onDownloadFallback: PropTypes.func,
  title: PropTypes.string,
  file: PropTypes.object,
  loading: PropTypes.bool,
  onViewedFileCallback: PropTypes.func
};

export default DocumentValidationModal;
